import React, { useState, useRef, useEffect } from "react";
import Toast from "src/components/Toast";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalInfoCard,
    StandardModalLabel,
    StandardModalText,
} from "src/components";

import { SubText, RowHeader, CountText,
    DocumentGrid, DocumentItem, DocumentTitle,
    DocumentCard, UploadButton, UploadIcon,
    UploadText, ActionRow, ViewButton,
    RemoveButton, IconBtn, FileRow,
    FileName, ModalOverlay, ModalBox,
    ModalClose, TitleRow,
    PreviewImage, UploadedByText, RadioWrapper,
    RadioCircle, RadioCheckIcon
} from "src/components/Documents/DocumentUploader.style";

import AddIcon from "src/img/new/add.svg";
import RedoIcon from "src/img/new/refresh-icon.png";
import DownloadIcon from "src/img/download-white2.png";
import CheckIcon from "src/img/Green-Check.svg";
import CloseIcon from "src/img/new/close-white.png";
import BlueCheck from "src/img/Blue-check.svg";

import {
    approveUserDocument,
    deleteUserDocument,
    rejectUserDocument,
    unapproveUserDocument,
} from "src/components/Documents/api/documentsApi";

import {
    EMPLOYEE_MODE_SOURCE,
    getPreviewUrl,
} from "./utils";

export default function EmployeeDocumentUploader({
    requiredDocuments = [],
    title = "Documents Uploaded",
    color,
    mode = EMPLOYEE_MODE_SOURCE.USER_LIST,
    onChange,
    refresh,
}) {

    const inputRefs = useRef([]);

    const [documents, setDocuments] = useState([]);
    const [previewFile, setPreviewFile] = useState(null);

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeIndex, setRemoveIndex] = useState(null);
    const [selectedIndexes, setSelectedIndexes] = useState([]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        onChange?.({
            documents,
        });
    }, [documents]);

    useEffect(() => {

        const initialDocs = requiredDocuments.map((doc) => {

            // uploaded document exists
            if (doc.upload?.url) {
                return {
                    uuid: doc.uuid,
                    uploadUuid: doc.upload.uuid,
                    name: decodeURIComponent(
                        doc.upload.url.split("/").pop()
                    ),
                    url: doc.upload.url,
                    uploaded: true,
                    approvedAt: doc.upload.approvedAt,
                    approvedBy: doc.upload.approvedBy,
                    uploadedBy: doc.upload?.uploadedBy
                };
            }
            return null;
        });

        setDocuments(initialDocs);
        setSelectedIndexes(
            initialDocs
                .map((doc, index) => doc?.approvedAt ? index : null)
                .filter(index => index !== null)
        );

        inputRefs.current = [];

    }, [requiredDocuments]);

    const handleUpload = (index, file) => {
        if (!file) return;

        const updated = [...documents];
        updated[index] = file;
        setDocuments(updated);
    };

    const handleInstantRemove = (index) => {

        setDocuments(prev => {
            const copy = [...prev];
            copy[index] = null;

            return copy;
        });
    };

    const openRemoveModal = (index) => {

        const doc = documents[index];
        const isFromApi = !!doc?.uuid;

        // newly uploaded only
        if (!isFromApi) {
            handleInstantRemove(index);
            return;
        }

        setRemoveIndex(index);
        setShowRemoveModal(true);
    };

    const cancelRemove = () => {
        setShowRemoveModal(false);
        setRemoveIndex(null);
    };

    const confirmRemove = async () => {
        const doc = documents[removeIndex];
        if (!doc?.uuid) return;

        try {
            setLoading(true);
            if ([EMPLOYEE_MODE_SOURCE.DASHBOARD, EMPLOYEE_MODE_SOURCE.SETTINGS].includes(mode)) {
                await deleteUserDocument(doc.uuid);
            } else {
                await rejectUserDocument(doc.uploadUuid);
            }
            Toast.success("Document Deleted Successfully");
            setShowRemoveModal(false);
            setRemoveIndex(null);

            setDocuments(prev => {
                const copy = [...prev];
                copy[removeIndex] = null;

                return copy;
            });

            await refresh?.();
        } catch (err) {
            console.error("Delete failed:", err);
            Toast.error("Failed to delete document");
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (index) => {
        if (mode !== EMPLOYEE_MODE_SOURCE.USER_LIST) return;

        const doc = documents[index];
        if (!doc?.uploadUuid) return;

        const isSelected = selectedIndexes.includes(index);
        try {
            if (isSelected) {
                await unapproveUserDocument(doc.uploadUuid);
                setSelectedIndexes(prev => prev.filter(i => i !== index));
                setDocuments(prev => {
                    const copy = [...prev];
                    copy[index] = {
                        ...copy[index],
                        approvedAt: null,
                        approvedBy: null
                    };
                    return copy;
                });
                Toast.success("Approval removed successfully");
            } else {
                const res = await approveUserDocument(doc.uploadUuid);
                setSelectedIndexes(prev => [...prev, index]);
                setDocuments(prev => {
                    const copy = [...prev];
                    copy[index] = {
                        ...copy[index],
                        approvedAt: res.data.document?.approvedAt || new Date(),
                        approvedBy: res.data.document?.approvedBy
                    };
                    return copy;
                });
                Toast.success("Document approved successfully");
            }
        } catch (err) {
            console.error(err);
            Toast.error("Unable to update document approval");
        }
    };

    const handleDownload = async (file) => {
        if (!file) return;

        const url = file?.url || getPreviewUrl(file);
        const filename = file?.name || "document";

        try {
            const response = await fetch(url, {
                method: "GET",
                mode: "cors"
            });

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
            Toast.error("Failed to download file");
        }
    };

    const handleView = async (file) => {
        if (!file) return;

        const url = getPreviewUrl(file);
        if (!url) return;

        const cleanUrl = decodeURIComponent(
            url.split("?")[0]
        );

        const isImage =
            /\.(jpg|jpeg|png|gif|webp)$/i.test(cleanUrl);

        const isPdf =
            /\.pdf$/i.test(cleanUrl);

        if (isImage) {
            setPreviewFile(file);
            return;
        }

        if (!isPdf) {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        try {
            const response = await fetch(url);
            const contentType =
                response.headers.get("Content-Type") || "";

            if (contentType.includes("application/pdf")) {
                window.open(url, "_blank", "noopener,noreferrer");
                return;
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(
                new Blob([blob], { type: "application/pdf" })
            );

            window.open(blobUrl, "_blank", "noopener,noreferrer");

            setTimeout(() => {
                URL.revokeObjectURL(blobUrl);
            }, 10000);
        } catch (err) {
            console.error("Preview failed:", err);
        }
    };

    const mergedDocuments = requiredDocuments.map((reqDoc, index) => {

        return {
            requiredDoc: reqDoc,
            doc: documents[index] || null,
            index
        };
    });

    return (
        <>
            {/* HEADER */}
            <RowHeader>
                <SubText
                    weight={700}
                    color={color || "#171A22"}
                >
                    {title}
                </SubText>

                <CountText size="16px" lineHeight="18px">
                    {documents.filter(Boolean).length} of {requiredDocuments.length}
                </CountText>
            </RowHeader>

            {/* GRID */}
            <DocumentGrid>
                {mergedDocuments.map((item) => {
                    const { requiredDoc, doc, index } = item;
                    const uploadedByName = doc?.uploadedBy?.employeeName;
                    const isChecked = selectedIndexes.includes(index) || doc?.approvedAt;

                    return (
                        <DocumentItem key={requiredDoc?.uuid || index}>
                            <TitleRow>
                                <DocumentTitle>
                                    {requiredDoc?.description ||
                                        `Document ${index + 1}`}
                                </DocumentTitle>
                            </TitleRow>

                            <div style={{ display: "flex", justifyContent: "space-between", gap: "20px" }}>
                                <div style={{ flex: 1 }}>
                                    <DocumentCard>
                                        {!doc ? (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <UploadButton
                                                    onClick={() =>
                                                        inputRefs.current[index]?.click()
                                                    }
                                                >
                                                    <UploadIcon src={AddIcon} />
                                                    <UploadText> Upload File  </UploadText>
                                                </UploadButton>
                                            </div>
                                        ) : (
                                            <FileRow>
                                                <FileName title={doc?.name}>
                                                    {doc?.name}
                                                </FileName>
                                                <ActionRow
                                                    style={{ gap: "15px" }}
                                                >
                                                    {mode === EMPLOYEE_MODE_SOURCE.DASHBOARD ? (
                                                        <>
                                                            <IconBtn
                                                                style={{
                                                                    padding: "2px",
                                                                    boxSizing: "border-box"
                                                                }}
                                                            >
                                                                <img
                                                                    onClick={() =>
                                                                        openRemoveModal(index)
                                                                    }
                                                                    src={CloseIcon}
                                                                    alt="close"
                                                                    style={{
                                                                        width: "14px",
                                                                        height: "14px",
                                                                        objectFit: "contain",
                                                                    }}
                                                                />
                                                            </IconBtn>

                                                            <IconBtn
                                                                onClick={() =>
                                                                    inputRefs.current[index]?.click()
                                                                }
                                                            >
                                                                <img
                                                                    src={RedoIcon}
                                                                    style={{
                                                                        marginTop: "3px"
                                                                    }}
                                                                />
                                                            </IconBtn>

                                                            <img
                                                                src={CheckIcon}
                                                                width="26"
                                                                height="26"
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ViewButton
                                                                onClick={() =>
                                                                    handleView(doc)
                                                                }
                                                            >
                                                                View
                                                            </ViewButton>

                                                            {mode === EMPLOYEE_MODE_SOURCE.SETTINGS && (
                                                                <IconBtn
                                                                    onClick={() =>
                                                                        inputRefs.current[index]?.click()
                                                                    }
                                                                >
                                                                    <img
                                                                        src={RedoIcon}
                                                                        style={{
                                                                            marginTop: "3px"
                                                                        }}
                                                                    />
                                                                </IconBtn>
                                                            )}

                                                            <IconBtn
                                                                onClick={() =>
                                                                    handleDownload(doc)
                                                                }
                                                            >
                                                                <img
                                                                    src={DownloadIcon}
                                                                />
                                                            </IconBtn>
                                                        </>
                                                    )}

                                                </ActionRow>
                                            </FileRow>
                                        )}
                                    </DocumentCard>
                                </div>
                                {mode === EMPLOYEE_MODE_SOURCE.USER_LIST && doc && (
                                    <RadioWrapper onClick={() => handleSelect(index)}>
                                        {isChecked ? (
                                            <RadioCheckIcon src={BlueCheck} />
                                        ) : (
                                            <RadioCircle />
                                        )}
                                    </RadioWrapper>
                                )}
                            </div>

                            {uploadedByName && (
                                <UploadedByText>
                                    Uploaded By {uploadedByName}
                                </UploadedByText>
                            )}

                            {doc &&
                                mode !== EMPLOYEE_MODE_SOURCE.DASHBOARD && (
                                <RemoveButton
                                    onClick={() =>
                                        openRemoveModal(index)
                                    }
                                >
                                    Remove Document
                                </RemoveButton>
                            )}

                            <input
                                type="file"
                                ref={(el) =>
                                    (inputRefs.current[index] = el)
                                }
                                style={{ display: "none" }}
                                onChange={(e) =>
                                    handleUpload(
                                        index,
                                        e.target.files[0]
                                    )
                                }
                            />
                        </DocumentItem>
                    );
                })}
            </DocumentGrid>

            {/* PREVIEW MODAL */}
            {previewFile && (
                <ModalOverlay
                    onClick={() => setPreviewFile(null)}
                >
                    <ModalBox
                        onClick={(e) => e.stopPropagation()}
                    >

                        <ModalClose
                            onClick={() => setPreviewFile(null)}
                        >
                            ×
                        </ModalClose>

                        <PreviewImage
                            src={getPreviewUrl(previewFile)}
                        />

                    </ModalBox>
                </ModalOverlay>
            )}

            {/* REMOVE MODAL */}
            {showRemoveModal && (
                <StandardModal
                    open={showRemoveModal}
                    onClose={cancelRemove}
                    title="Remove Document"
                    description="The file will be deleted permanently, and a new file will need to be uploaded to meet requirements."
                    widthMode="medium"
                    footer={(
                        <StandardModalActionGroup $pushRight>
                            <StandardModalActionButton
                                onClick={cancelRemove}
                                border="1px solid #171A22"
                                color="#171A22"
                            >
                                Cancel
                            </StandardModalActionButton>
                            <StandardModalActionButton
                                $variant="primary"
                                onClick={confirmRemove}
                                disabled={loading}
                            >
                                {loading ? "Deleting.." : "Confirm"}
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    )}
                >
                    {removeIndex !== null && (
                        <StandardModalInfoCard>
                            <StandardModalLabel as="div">
                                {requiredDocuments[removeIndex]?.description}
                            </StandardModalLabel>
                            <StandardModalText>
                                {documents[removeIndex]?.name}
                            </StandardModalText>
                        </StandardModalInfoCard>
                    )}
                </StandardModal>
            )}
        </>
    );
}
